"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Mountain, Camera, Cloud, ShoppingBag, Map as MapIcon, Utensils, Tent, Gift, Clock, CalendarDays, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const tabsData = [{
  id: "3-days",
  label: "Concept",
  value: "3-days"
}, {
  id: "7-days",
  label: "Production",
  value: "7-days"
}, {
  id: "14-days",
  label: "Post-Prod",
  value: "14-days"
}];
const timelineStops = [{
  id: 1,
  tabId: "3-days",
  day: "Phase 01",
  time: "Strategy",
  title: "Creative Briefing",
  description: "We define the visual language, target audience, and core message to ensure every frame serves your brand's objective.",
  icon: "MapPin"
}, {
  id: 2,
  tabId: "3-days",
  day: "Phase 02",
  time: "Scripting",
  title: "Narrative Architecture",
  description: "Crafting a compelling story that demands attention, focusing on cinematic pacing and emotional resonance.",
  icon: "Mountain"
}, {
  id: 3,
  tabId: "3-days",
  day: "Phase 03",
  time: "Visuals",
  title: "Storyboarding & Style",
  description: "Developing high-contrast moodboards and shot lists that establish the 'Pivotal' aesthetic for your project.",
  icon: "Camera"
}, {
  id: 4,
  tabId: "7-days",
  day: "Phase 01",
  time: "Setup",
  title: "Technical Staging",
  description: "Precision lighting and camera configuration using industry-leading gear to capture high-grain, artistry visuals.",
  icon: "Mountain"
}, {
  id: 5,
  tabId: "7-days",
  day: "Phase 02",
  time: "Principal",
  title: "Cinematic Capture",
  description: "The main shoot where our directors bring the storyboard to life with meticulous attention to detail and artistry.",
  icon: "Cloud"
}, {
  id: 6,
  tabId: "7-days",
  day: "Phase 03",
  time: "Audio",
  title: "Sound Acquisition",
  description: "Capturing crisp, immersive audio that complements the visual intensity of the high-end production.",
  icon: "ShoppingBag"
}, {
  id: 7,
  tabId: "7-days",
  day: "Phase 04",
  time: "Wrap",
  title: "Data Management",
  description: "Secure offloading and redundant backup of all raw 8K footage to ensure your assets are protected.",
  icon: "MapIcon"
}, {
  id: 8,
  tabId: "14-days",
  day: "Phase 01",
  time: "Assembly",
  title: "The Rough Cut",
  description: "Initial edit focusing on rhythm and flow, establishing the foundational structure of the cinematic narrative.",
  icon: "MapPin"
}, {
  id: 9,
  tabId: "14-days",
  day: "Phase 02",
  time: "Color",
  title: "Anodized Grading",
  description: "Applying our signature high-contrast color grade to give the footage a premium, filmic texture.",
  icon: "Utensils"
}, {
  id: 10,
  tabId: "14-days",
  day: "Phase 03",
  time: "VFX",
  title: "Motion Graphics",
  description: "Integrating minimalist, geometric overlays and visual effects that enhance the brand's digital presence.",
  icon: "Tent"
}, {
  id: 11,
  tabId: "14-days",
  day: "Phase 04",
  time: "Master",
  title: "Final Delivery",
  description: "Exporting optimized versions for web, social, and stream, ready to capture global attention.",
  icon: "Gift"
}];
const iconMap: Record<string, React.ElementType> = {
  MapPin,
  Mountain,
  Camera,
  Cloud,
  ShoppingBag,
  MapIcon,
  Utensils,
  Tent,
  Gift
};
export default function PerfectItinerarySuggestions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="222937" id="the-production-pipeline" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-3xl md:text-4xl lg:text-6xl font-bold text-foreground mb-4 tracking-tighter uppercase">
            The Production Pipeline
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl font-mono">
            A technical breakdown of how we transform brand concepts into cinematic artistry. Precision in every frame.
          </p>
        </div>

        {}
        <div ref={contentRef} className={`transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Tabs defaultValue="3-days" className="w-full">
            <div className="flex justify-center mb-10">
              <TabsList className="grid w-full max-w-md grid-cols-3 p-1 bg-secondary rounded-none border border-border">
                {tabsData.map((tab, i) => <TabsTrigger key={tab.id} value={tab.value} data-index={i} className="rounded-none font-mono font-medium text-xs py-2.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none transition-all uppercase tracking-widest">
                    {tab.label}
                  </TabsTrigger>)}
              </TabsList>
            </div>

            {tabsData.map((tab, tabIndex) => {
            const stops = timelineStops.filter(stop => stop.tabId === tab.value);
            return <TabsContent key={tab.id} value={tab.value} data-index={tabIndex} className="mt-0 outline-none focus-visible:ring-0">
                  <div className="relative pl-10 md:pl-16 py-4 max-w-3xl mx-auto">
                    {}
                    <Separator orientation="vertical" className="absolute left-[19px] md:left-[31px] top-8 bottom-8 w-[1px] bg-primary/40" />

                    <div className="space-y-8 md:space-y-12">
                      {stops.map((stop, i) => {
                    const IconComponent = iconMap[stop.icon] || MapPin;
                    return <div key={stop.id} data-index={i} className="relative group">
                            {}
                            <div className="absolute -left-10 md:-left-16 top-1 md:top-2 flex size-10 md:size-12 items-center justify-center rounded-none bg-background border border-primary/50 text-primary shadow-none group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-colors duration-300 z-10">
                              <IconComponent className="size-5 md:size-6" />
                            </div>

                            {}
                            <Card className="border-border rounded-none shadow-none hover:bg-secondary/30 transition-colors duration-300 bg-card/40 backdrop-blur-none">
                              <CardHeader className="pb-3">
                                <div className="flex flex-wrap items-center gap-3 mb-2">
                                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-none bg-primary text-primary-foreground text-[10px] font-mono uppercase tracking-widest">
                                    <CalendarDays className="size-3" />
                                    {stop.day}
                                  </div>
                                  <div className="inline-flex items-center gap-1.5 text-muted-foreground text-xs font-mono uppercase">
                                    <Clock className="size-3" />
                                    {stop.time}
                                  </div>
                                </div>
                                <CardTitle className="font-default text-xl md:text-2xl text-foreground uppercase tracking-tight">
                                  {stop.title}
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <CardDescription className="text-base text-muted-foreground font-mono leading-relaxed">
                                  {stop.description}
                                </CardDescription>
                              </CardContent>
                            </Card>
                          </div>;
                  })}
                    </div>
                  </div>
                </TabsContent>;
          })}
          </Tabs>
        </div>

        {}
        <div className="mt-16 text-center">
          <Button asChild size="lg" className="rounded-none px-10 font-default text-sm h-14 shadow-none bg-primary text-primary-foreground hover:opacity-90 transition-all uppercase tracking-widest">
            <Link to="/StartProjectPage">
              Start a Project
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}